/**
 * PDP Phase 2 behaviour: the features carousel, the setup-video facade and
 * the accessory add-to-cart buttons.
 *
 * Everything is a custom element so each section boots itself and keeps
 * working after a theme-editor re-render.
 */
(function () {
  'use strict';

  const log = (...args) => {
    if (window.Shopify && window.Shopify.designMode) console.warn('[pdp-phase2]', ...args);
  };

  /* ------------------------------------------------------------------ *
   * Features carousel
   * ------------------------------------------------------------------ */
  if (!customElements.get('pdp-features-carousel')) {
    customElements.define(
      'pdp-features-carousel',
      class PdpFeaturesCarousel extends HTMLElement {
        connectedCallback() {
          this.viewport = this.querySelector('[data-pdp-viewport]');
          this.prev = this.querySelector('[data-pdp-prev]');
          this.next = this.querySelector('[data-pdp-next]');
          if (!this.viewport) return;

          this.onScroll = this.throttle(this.syncNav.bind(this));
          this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });

          if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
          if (this.next) this.next.addEventListener('click', () => this.step(1));

          this.syncNav();
        }

        disconnectedCallback() {
          if (this.viewport) this.viewport.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }

        // One step is a card plus its gap, read from the rendered layout so it
        // stays correct at every viewport width.
        get stepSize() {
          const cards = this.viewport.querySelectorAll('[data-pdp-card]');
          if (cards.length < 2) return cards.length ? cards[0].offsetWidth : 0;
          return cards[1].offsetLeft - cards[0].offsetLeft;
        }

        step(direction) {
          const size = this.stepSize;
          if (!size) return;
          this.viewport.scrollBy({ left: size * direction, behavior: 'smooth' });
        }

        syncNav() {
          const max = this.viewport.scrollWidth - this.viewport.clientWidth;
          const left = this.viewport.scrollLeft;
          if (this.prev) this.prev.toggleAttribute('hidden', left <= 2);
          if (this.next) this.next.toggleAttribute('hidden', left >= max - 2);
        }

        throttle(fn) {
          let frame = null;
          return () => {
            if (frame) return;
            frame = requestAnimationFrame(() => {
              frame = null;
              fn();
            });
          };
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Hero video — attach the source on approach, not at page load
   * ------------------------------------------------------------------ */
  if (!customElements.get('pdp-lazy-video')) {
    customElements.define(
      'pdp-lazy-video',
      class PdpLazyVideo extends HTMLElement {
        connectedCallback() {
          this.video = this.querySelector('video');
          if (!this.video || !this.video.dataset.pdpSrc) return;

          if (!('IntersectionObserver' in window)) return this.load();

          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) this.load();
            },
            { rootMargin: '400px 0px' }
          );
          this.observer.observe(this);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load() {
          if (this.loaded) return;
          this.loaded = true;
          if (this.observer) this.observer.disconnect();

          this.video.src = this.video.dataset.pdpSrc;
          // Autoplay can still be refused (data saver, low power mode); the
          // poster stays visible in that case, which is the right fallback.
          const played = this.video.play();
          if (played && played.catch) played.catch(() => {});
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Setup video — click-to-play facade so the embed only loads on demand
   * ------------------------------------------------------------------ */
  if (!customElements.get('pdp-setup-video')) {
    customElements.define(
      'pdp-setup-video',
      class PdpSetupVideo extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('[data-pdp-play]');
          if (!this.button) return;
          this.button.addEventListener('click', this.play.bind(this), { once: true });
        }

        play() {
          const videoId = this.dataset.videoId;
          if (!videoId) return log('setup video has no video id');

          const iframe = document.createElement('iframe');
          iframe.src =
            'https://www.youtube-nocookie.com/embed/' +
            encodeURIComponent(videoId) +
            '?autoplay=1&rel=0&playsinline=1';
          iframe.title = this.dataset.videoTitle || 'Setup video';
          iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
          iframe.allowFullscreen = true;
          iframe.setAttribute('loading', 'lazy');

          this.button.replaceChildren(iframe);
          this.button.style.cursor = 'default';
          this.closest('.pdp-setup').classList.add('is-playing');
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Accessory add-to-cart
   * ------------------------------------------------------------------ */
  if (!customElements.get('pdp-accessory-add')) {
    customElements.define(
      'pdp-accessory-add',
      class PdpAccessoryAdd extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.defaultLabel = this.button.textContent.trim();
          this.button.addEventListener('click', this.add.bind(this));
        }

        get cart() {
          return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }

        async add() {
          if (this.button.getAttribute('aria-disabled') === 'true') return;
          const variantId = this.dataset.variantId;
          if (!variantId) return log('accessory button has no variant id');

          this.button.setAttribute('aria-disabled', 'true');
          this.button.classList.add('loading');

          const body = new FormData();
          body.append('id', variantId);
          body.append('quantity', 1);

          const cart = this.cart;
          if (cart && typeof cart.getSectionsToRender === 'function') {
            body.append('sections', cart.getSectionsToRender().map((section) => section.id));
            body.append('sections_url', window.location.pathname);
            if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
          }

          try {
            const response = await fetch(window.routes.cart_add_url, {
              method: 'POST',
              headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
              body: body,
            });
            const data = await response.json();

            if (data.status) throw new Error(data.description || data.message);

            if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'pdp-accessories',
                productVariantId: variantId,
                cartData: data,
              });
            }

            this.button.textContent = this.dataset.addedLabel || 'ADDED';

            if (cart && typeof cart.renderContents === 'function') {
              if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
              cart.renderContents(data);
            } else {
              window.location = window.routes.cart_url;
            }
          } catch (error) {
            log(error);
            this.button.textContent = this.defaultLabel;
            this.button.removeAttribute('aria-disabled');
          } finally {
            this.button.classList.remove('loading');
          }
        }
      }
    );
  }
})();
